import {isEmpty, isNotEmpty, isNotNil} from 'ramda';

import type {FilterValues} from '@dms/api/core';
import type {GetSettingsServiceOrderVariantsApiResponse} from '@dms/api/workshop';

import {WORKSHOP_SERVICE_CASE_GRID_CODE} from '../constants/workshopOverviewTiles';
import type {WorkshopDashboardFilters} from '../types/WorkshopDashboardFilters';
import type {WorkshopOverviewTileConfig} from '../types/WorkshopOverviewTileConfig';

type WorkshopTileVariant = Pick<
  NonNullable<GetSettingsServiceOrderVariantsApiResponse[number]>,
  'id' | 'serviceOrderTypeId'
>;

export function getWorkshopTileFilters(
  tile: WorkshopOverviewTileConfig,
  filters: WorkshopDashboardFilters,
  orderTypeCodes: string[],
  variants?: WorkshopTileVariant[]
): FilterValues | null {
  const branchFilters: FilterValues = isNotEmpty(filters.branchId)
    ? {branch: [...filters.branchId].sort()}
    : {};

  const isServiceCase = tile.gridCode === WORKSHOP_SERVICE_CASE_GRID_CODE;
  const keys = isServiceCase
    ? {type: 'serviceOrderType', variant: 'serviceOrderVariant'}
    : {type: 'orderType', variant: 'orderVariant'};

  const hasTypes = isNotEmpty(filters.serviceOrderTypeId);
  const hasVariants = isNotEmpty(filters.serviceOrderVariantId);

  const fixedTypes = tile.filters[keys.type];
  const selectedTypes = Array.isArray(fixedTypes)
    ? orderTypeCodes.filter((code) => fixedTypes.includes(code))
    : orderTypeCodes;

  // The service-case grid matches type and variant on any two orders of a case, the overview on one
  // order. Each variant has one type, so narrowing variants to the selected types keeps the overview count.
  // Without every selected variant's type (list failed, variant deleted), both filters still apply.
  const selectedVariantDetails = filters.serviceOrderVariantId.map((id) =>
    variants?.find((variant) => variant.id === id)
  );
  const narrowsVariants =
    isServiceCase && hasTypes && hasVariants && selectedVariantDetails.every(isNotNil);
  const selectedVariants = narrowsVariants
    ? selectedVariantDetails.flatMap((variant) =>
        isNotNil(variant) && filters.serviceOrderTypeId.includes(variant.serviceOrderTypeId)
          ? [variant.id]
          : []
      )
    : filters.serviceOrderVariantId;

  // Empty DG arrays are unrestricted, so incompatible selections cannot open this tile.
  if (
    (hasTypes &&
      (orderTypeCodes.length !== filters.serviceOrderTypeId.length || isEmpty(selectedTypes))) ||
    (hasVariants && isEmpty(selectedVariants))
  ) {
    return null;
  }

  return {
    ...tile.filters,
    ...branchFilters,
    ...(hasTypes ? {[keys.type]: [...selectedTypes].sort()} : {}),
    ...(hasVariants ? {[keys.variant]: [...selectedVariants].sort()} : {}),
  };
}
